import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, afterNextRender, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import { LegalLinksService } from '../../core/legal-links.service';
import { RegistrationFormsService } from '../forms/registration-forms.service';
import { Country, TimeZone } from '../register/register';
import { RegistrationService } from '../registration.service';
import { CompanyDomainSync } from '../ui/company-domain-sync/company-domain-sync';
import { Error500 } from '../ui/error500/error500';
import { ErrorDisplayer } from '../ui/error-displayer/error-displayer';
import { Turnstile } from '../ui/turnstile/turnstile';
import { VcInput } from '../ui/vc-input/vc-input';

const SECTORS = ['hotel', 'spa', 'leisure', 'restaurant', 'retail', 'other'];

@Component({
  selector: 'app-step2',
  imports: [ReactiveFormsModule, TranslatePipe, VcInput, ErrorDisplayer, CompanyDomainSync, Turnstile, Error500],
  templateUrl: './step2.html',
  styleUrl: './step2.scss',
})
export class Step2 implements OnInit {
  readonly countries = input.required<Country[]>();
  readonly timeZones = input.required<Record<string, TimeZone[]>>();
  readonly currencies = input.required<string[]>();
  readonly back = output();
  readonly submitted = output<string>();

  private readonly forms = inject(RegistrationFormsService);
  private readonly registration = inject(RegistrationService);
  private readonly turnstile = viewChild(Turnstile);

  protected readonly form = this.forms.step2;
  protected readonly submitting = this.forms.submitting;
  protected readonly termsUrl = inject(LegalLinksService).termsUrl;
  protected readonly cookiePolicyUrl = inject(LegalLinksService).cookiePolicyUrl;
  protected readonly sectors = SECTORS;

  protected readonly allZones = computed(() =>
    Object.values(this.timeZones())
      .flat()
      .sort((a, b) => a.key.localeCompare(b.key)),
  );
  protected readonly zones = signal<TimeZone[] | null>(null);
  protected readonly showTimezoneSelect = signal(true);
  protected readonly show500 = signal(false);
  protected readonly turnstileError = signal(false);
  protected readonly focused = signal<string | null>(null);
  turnstileEnabled = Turnstile.enabled;

  constructor() {
    const { country, companyName, domain } = this.form.controls;
    country.valueChanges.pipe(takeUntilDestroyed()).subscribe((code) => {
      this.updateTimezones(code);
      this.updateSaleCurrency(code);
    });
    companyName.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe((value) => this.pushToDataLayer({ companyName: value }));
    domain.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe((value) => this.pushToDataLayer({ salesUrl: value }));
    afterNextRender(() => setTimeout(() => document.getElementById('businessSector')?.focus(), 300));
  }

  ngOnInit() {
    this.preselectCountry();
  }

  protected isCountryError() {
    return this.form.controls.country.hasError('countryInvalid');
  }

  async submitForm() {
    if (this.submitting()) {
      return;
    }
    if (this.form.invalid) {
      this.forms.markStepTouched(2);
      return;
    }
    if (this.turnstileEnabled && !this.registration.turnstileToken) {
      this.turnstileError.set(true);
      return;
    }
    this.submitting.set(true);
    this.forms.setDisabled(true);
    try {
      this.submitted.emit(await this.registration.submit());
    } catch (error) {
      this.forms.setDisabled(false);
      this.submitting.set(false);
      const response = error as HttpErrorResponse;
      this.turnstileError.set(!!response.error?.errors?.turnstileToken);
      this.turnstile()?.reset();
      this.handleError(response);
    }
  }

  onTurnstileToken(token: string | null) {
    this.registration.turnstileToken = token;
    if (token) {
      this.turnstileError.set(false);
    }
  }

  private updateTimezones(code: string) {
    const zones = this.timeZones()[code];
    if (!zones) {
      this.showTimezoneSelect.set(true);
      this.zones.set(null);
      return;
    }
    this.form.controls.timeZone.setValue(zones[0].key);
    this.showTimezoneSelect.set(zones.length > 1);
    this.zones.set(zones);
  }

  private updateSaleCurrency(code: string) {
    const country = this.countries().find((c) => c.code.toLowerCase() === code.toLowerCase());
    const currency = country && this.currencies().includes(country.defaultCurrency) ? country.defaultCurrency : 'USD';
    this.form.controls.saleCurrency.setValue(currency);
  }

  private preselectCountry() {
    const language = navigator.language;
    const fromBrowser = language.length === 2 ? language : language.slice(3).length === 2 ? language.slice(3) : '';
    const code = (this.form.controls.country.value || fromBrowser).toLowerCase();
    if (code && this.countries().some((c) => c.code.toLowerCase() === code)) {
      this.form.controls.country.setValue(code);
      this.form.controls.country.markAsDirty();
      this.form.controls.country.markAsTouched();
    }
  }

  /** The API answers 422 with `{ errors: { field: [message] } }`; the first message decides which field is wrong. */
  private handleError(response: HttpErrorResponse) {
    if (response.status >= 200 && response.status < 300) {
      // a 2xx whose body could not be read (HttpErrorResponse.ok is always false): the store got created
      this.submitted.emit(this.registration.storeSignInUrl(this.form.controls.domain.value));
      return;
    }
    const message = this.firstErrorMessage(response);
    if (message === undefined) {
      // 5xx html page, network error, unknown shape: the user must be able to try again
      this.show500.set(true);
      return;
    }
    if (response.status === 500) {
      this.show500.set(true);
    }
    if (message.includes('sale page')) {
      this.form.controls.domain.setErrors({ domainExist: true });
    }
    if (message.includes('email')) {
      this.forms.step1.controls.email.setErrors({ emailInvalid: true });
    }
    if (message.includes('country')) {
      this.form.controls.country.setErrors({ countryInvalid: true });
    }
  }

  private firstErrorMessage(response: HttpErrorResponse): string | undefined {
    const errors = response.error?.errors;
    const first = errors && typeof errors === 'object' ? Object.values(errors)[0] : undefined;
    const message = Array.isArray(first) ? first[0] : undefined;
    return typeof message === 'string' ? message.toLowerCase() : undefined;
  }

  private pushToDataLayer(entry: Record<string, string>) {
    (window as unknown as { dataLayer?: unknown[] }).dataLayer?.push(entry);
  }
}
